/**
 * API integration auth.type validator — enforces canonical auth type literals
 * against generated `api-config-*.json` artifacts at artifact-write time.
 *
 * Same contract-drift bug class as swp-l6yu (Pulse content schema validator).
 * The api-otter emitted `auth.type: api-key` (hyphenated) for runs 5–8/8 of
 * the disaster-health-logistics project. This validator catches the non-canonical
 * literal at validate_artifact write-time and emits an actionable retryPrompt
 * naming the file, yaml-path, bad value, and rename suggestion — letting the
 * otter self-correct before the artifact is written.
 *
 * Wired into pipeline.ts _validateArtifactInner for phase: 'api' (step 5.6).
 */

// ---------------------------------------------------------------------------
// Canonical auth type enum
//
// Source of truth: consuming code in packages/openapi —
//   CollectionProviderGenerator.ts:  'bearer' | 'apiKey' | 'oauth2'
//   OpenAPIPlugin.ts:                'bearer' | 'apiKey' | 'oauth2'
//   Real artifacts:                  'none' also appears (no-auth integrations)
//   Planning spec:                   'oidc' listed as rename target → include
// ---------------------------------------------------------------------------

export const CANONICAL_API_AUTH_TYPES = ['apiKey', 'bearer', 'oauth2', 'oidc', 'none'] as const;

export type CanonicalApiAuthType = (typeof CANONICAL_API_AUTH_TYPES)[number];

const CANONICAL_SET = new Set<string>(CANONICAL_API_AUTH_TYPES);

const VALID_SET_DISPLAY = CANONICAL_API_AUTH_TYPES.map((t) => `\`${t}\``).join(', ');

// ---------------------------------------------------------------------------
// Rename map — non-canonical literals → canonical equivalent
//
// Keys are the bad literal exactly as written OR lowercased for case-insensitive
// matching. See validateAuthType() for the lookup strategy.
// ---------------------------------------------------------------------------

const RENAME_MAP: Record<string, CanonicalApiAuthType> = {
  // apiKey variants
  'api-key': 'apiKey',
  api_key: 'apiKey',
  apikey: 'apiKey', // lowercase of 'apiKey' / 'APIKEY'

  // oauth2 variants
  'oauth-2': 'oauth2',
  oauth_2: 'oauth2',

  // oidc variants
  'open-id-connect': 'oidc',
  'openid-connect': 'oidc',
  openidconnect: 'oidc', // lowercase of 'openIdConnect'
  openidconnect_: 'oidc', // belt-and-suspenders
  openIdConnect: 'oidc', // exact camelCase as emitted by some tooling

  // bearer variants
  'bearer-token': 'bearer',
  bearer_token: 'bearer',
};

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ApiIntegrationValidationResult {
  valid: boolean;
  retryPrompt?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Look up the canonical rename suggestion for a bad literal.
 * Tries exact match first, then lowercased — handles both camelCase originals
 * (openIdConnect) and ALL-CAPS user typos (API-KEY).
 */
function renameSuggestion(badLiteral: string): CanonicalApiAuthType | undefined {
  return RENAME_MAP[badLiteral] ?? RENAME_MAP[badLiteral.toLowerCase()];
}

/**
 * Validate a single auth.type value.
 * Returns a formatted error string if the value is non-canonical, null if ok.
 *
 * @param authType     The value found at `yamlPath` in the artifact.
 * @param yamlPath     jq-style path string (e.g. `auth.type`).
 * @param artifactPath Relative path to the artifact file for retryPrompt context.
 */
function validateAuthType(
  authType: unknown,
  yamlPath: string,
  artifactPath: string
): string | null {
  if (authType === undefined || authType === null) return null; // absent → no auth config, fine

  if (typeof authType !== 'string') {
    return [
      `${artifactPath}: \`${yamlPath}\` must be a string, got \`${typeof authType}\`.`,
      `Valid values: ${VALID_SET_DISPLAY}.`,
      'Fix this value and re-call stackwright_pro_validate_artifact.',
    ].join('\n');
  }

  if (CANONICAL_SET.has(authType)) return null; // canonical

  const suggestion = renameSuggestion(authType);
  const lines: string[] = [
    `${artifactPath}: \`${yamlPath}\` has non-canonical value \`"${authType}"\`.`,
    `Valid values: ${VALID_SET_DISPLAY}.`,
  ];
  if (suggestion) {
    lines.push(`Did you mean: \`${suggestion}\`?`);
  }
  lines.push('Fix this value and re-call stackwright_pro_validate_artifact.');

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Validate an api-config artifact's auth blocks against the canonical
 * auth.type enum.
 *
 * Walks:
 *   - `artifact.auth.type`                      (top-level auth block)
 *   - `artifact.securitySchemes.<name>.type`    (OpenAPI-style named schemes)
 *
 * Per-endpoint auth is not present in the current api-config schema — skipped.
 *
 * @param artifact        The parsed artifact JSON. Accepts `unknown` so callers
 *                        don't need to cast.
 * @param integrationName Optional integration name; used to derive the artifact
 *                        file path in the retryPrompt (e.g. "openrouteservice").
 * @returns { valid, retryPrompt? }
 */
export function validateApiIntegration(
  artifact: unknown,
  integrationName?: string
): ApiIntegrationValidationResult {
  const artifactFile = integrationName ? `api-config-${integrationName}.json` : 'api-config.json';
  const artifactPath = `.stackwright/artifacts/${artifactFile}`;

  if (artifact === null || typeof artifact !== 'object') {
    return { valid: true }; // not an object — structural validator will catch it
  }

  const obj = artifact as Record<string, unknown>;
  const errors: string[] = [];

  // 1. Top-level auth.type
  if ('auth' in obj && obj['auth'] !== null && typeof obj['auth'] === 'object') {
    const auth = obj['auth'] as Record<string, unknown>;
    const err = validateAuthType(auth['type'], 'auth.type', artifactPath);
    if (err) errors.push(err);
  }

  // 2. securitySchemes.<name>.type (OpenAPI-style named-scheme dict)
  if (
    'securitySchemes' in obj &&
    obj['securitySchemes'] !== null &&
    typeof obj['securitySchemes'] === 'object' &&
    !Array.isArray(obj['securitySchemes'])
  ) {
    const schemes = obj['securitySchemes'] as Record<string, unknown>;
    for (const [schemeName, scheme] of Object.entries(schemes)) {
      if (scheme !== null && typeof scheme === 'object') {
        const s = scheme as Record<string, unknown>;
        const err = validateAuthType(s['type'], `securitySchemes.${schemeName}.type`, artifactPath);
        if (err) errors.push(err);
      }
    }
  }

  if (errors.length === 0) return { valid: true };

  const retryPrompt = [
    `API integration auth validation failed: ${errors.length} violation(s) in ${artifactPath}.`,
    '',
    'You MUST fix ALL violations and re-call stackwright_pro_validate_artifact.',
    '',
    ...errors.flatMap((e) => [e, '']),
    `Canonical auth.type values: ${VALID_SET_DISPLAY}.`,
    'Do NOT use hyphenated or underscored variants (api-key, oauth-2, bearer-token, etc.).',
  ].join('\n');

  return { valid: false, retryPrompt };
}
